import React, { useCallback, useMemo } from 'react';
import { getProjectState, setProjectState, setTempState, useUnitData, getViewport } from '@starflow/app/store';
import { getSharedStage } from '@starflow/app/components/canvas/stage';
import { convertMultiSelectionToImage } from '@starflow/app/helpers/element';
import { translate } from '@shakkerai/util-i18n';
import { Icon } from '@shakkerai/components';
import helpers from '@starflow/app/helpers';
import { AlignTool } from './AlignTool';
import { getActiveGeneratorNodeId } from '@starflow/app/helpers/flow';
import { convertGroupToCanvas } from '@shakkerai/pro-utils';
import { compressLocalImage } from '@shakkerai/util-image';
import uniqid from 'uniqid';
import {
  applyImg2ImgSourceImage,
  applyReferenceImage,
  applyColorReferenceFromImg,
} from '@starflow/app/modules/generator/helpers';
import { MODEL_SD35 } from '@starflow/app/modules/models';
import { mc } from '@shakkerai/report';
import makegroupIcon from './assets/make-group.svg';
import combineIcon from './assets/combine.svg';
import ungroupIcon from './assets/ungroup.svg';

const tempGroupToCanvas = async () => {
  // 先合并图片
  const { elements, selection } = getProjectState();
  const range = helpers.utils.getSelectionRange();
  const newCanvas = await convertGroupToCanvas({
    width: range.maxX - range.minX,
    height: range.maxY - range.minY,
    elements: elements.filter((item) => selection.includes(item.id)),
    range,
  });
  return newCanvas;
};

export const MuitlSelectionTools = ({ selection }) => {
  const { baseType, presetBaseModelId } = useUnitData();
  // SD35模型不支持作为参考图
  const disableReference = baseType === MODEL_SD35;
  // 仅支持星流
  const disableColorReference = presetBaseModelId !== 'base_checkpoint';
  const handleMergeSelection = useCallback(() => {
    convertMultiSelectionToImage(getProjectState('selection'));
  }, []);
  const handleSendToImg2Img = useCallback(
    async (event) => {
      // debugger
      mc('tool.canvas.img2img', { image_id: undefined });
      // todos 这里还不支持图形
      // const newCanvas = await tempGroupToCanvas();
      // const uploadRes = await uploadCanvas(newCanvas);
      // const generatorId = getActiveGeneratorNodeId();
      // const inputElement = {
      //   id: uniqid(),
      //   width: newCanvas.width,
      //   height: newCanvas.height,
      //   url: uploadRes.url,
      // };

      // const { scale } = getViewport();
      // const elements = getProjectState('elements');
      const { rect, file } = await helpers.canvas.selectionToFile();
      const inputElement = {
        id: uniqid(),
        width: rect.width,
        height: rect.height,
        url: file.url,
      };

      const generatorId = getActiveGeneratorNodeId();
      applyImg2ImgSourceImage(inputElement, generatorId);
      setTempState('exclusiveWindow', `image-input-options-${generatorId}`);
      event.stopPropagation();
    },
    [selection],
  );
  const handleSendToReference = useCallback(async () => {
    mc('tool.canvas.reference', { image_id: undefined });
    // const newCanvas = await tempGroupToCanvas();
    // const uploadRes = await uploadCanvas(newCanvas);
    // const inputElement = {
    //   id: uniqid(),
    //   width: newCanvas.width,
    //   height: newCanvas.height,
    //   url: uploadRes.url,
    // };
    const { rect, file } = await helpers.canvas.selectionToFile();
    const inputElement = {
      id: uniqid(),
      width: rect.width,
      height: rect.height,
      url: file.url,
    };
    applyReferenceImage(inputElement);
  }, []);
  const handleSendToColorReference = useCallback(async () => {
    // const newCanvas = await tempGroupToCanvas();
    // const uploadRes = await uploadCanvas(newCanvas);
    const { file } = await helpers.canvas.selectionToFile();
    const inputElement = {
      url: file.url,
    };
    applyColorReferenceFromImg(inputElement);
  }, []);

  const isGroup = useMemo(() => {
    if (selection.length === 1 && selection[0].startsWith('element-group')) {
      return true;
    }
    return false;
  }, [selection]);
  return (
    <div
      className={`absolute top-full left-1/2 -translate-x-1/2 flex flex-row items-center bg-white pl-4 pointer-events-auto`}
    >
      <AlignTool />
      <div className="w-px h-5 ml-1 bg-[#d9d9d9]"></div>
      {isGroup ? (
        <div className="flex items-center justify-center bg-white rounded-sm pointer-events-auto group-button-class">
          <button
            onClick={async () => {
              // 解除编组
              const selectedGroupElement = getProjectState().elements.filter((item) => item.id === selection[0]);
              const newelements = helpers.element.ungroupElements(selection);
              const newSelection = selectedGroupElement[0].elements.map((item) => item.id);
              await new Promise((resolve) => requestAnimationFrame(resolve));
              setProjectState({ elements: newelements, selection: newSelection });
            }}
            className="px-4 h-8 text-xs text-[#2d2d2d] whitespace-nowrap flex flex-row items-center group-button-class"
          >
            <img src={ungroupIcon} className="w-4 h-4" />
            {translate('menu_ungroup')}
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-center bg-white rounded-sm pointer-events-auto">
          <button
            onClick={() => {
              const [elements, group] = helpers.element.groupElements(selection);
              // console.log('xxx elements', elements, group.id);
              setProjectState({ elements: elements, selection: [group.id] });
            }}
            className="px-4 h-8 text-xs text-[#2d2d2d] whitespace-nowrap flex flex-row items-center group-button-class"
          >
            <img src={makegroupIcon} className="w-4 h-4" />
            {translate('common_group')}
          </button>
        </div>
      )}
      <div className="flex items-center justify-center bg-white rounded-sm pointer-events-auto group-button-class">
        <button
          onClick={handleMergeSelection}
          className="px-4 h-8 text-xs text-[#2d2d2d] whitespace-nowrap flex flex-row items-center"
        >
          <img src={combineIcon} className="w-4 h-4" />
          {translate('flattenImage')}
        </button>
      </div>
      <div className="flex items-center justify-center bg-white rounded-sm pointer-events-auto">
        <div
          className={`group relative flex items-center h-6 px-2 cursor-pointer mx-3 bg-[#f2f4f6] whitespace-nowrap text-xs`}
        >
          {translate('image_tool_use_image')}
          <Icon className="shk-kuangjia-xiala -mr-2" color="#666" fontSize={18} />
          <div className="absolute hidden group-hover:block left-0 top-[24px] flex flex-col items-stretch rounded-sm bg-[#2c2c2c] py-1.5 px-0">
            <button
              onClick={handleSendToImg2Img}
              className="flex flex-col w-full px-3 py-1.5 text-white hover:bg-primary hover:text-[#2c2c2c]"
            >
              <span className="text-xs leading-[16px] font-semibold">{translate('img_use_as_i2i_title')}</span>
              <span className="mt-1 leading-[12px] text-[11px]">{translate('img_use_as_i2i_desc')}</span>
            </button>
            <button
              disabled={disableReference}
              onClick={handleSendToReference}
              className="flex flex-col w-full px-3 py-1.5 text-white hover:bg-primary hover:text-[#2c2c2c] disabled:pointer-events-none disabled:opacity-30"
            >
              <span className="text-xs leading-[16px] font-semibold">{translate('img_use_as_ref_title')}</span>
              <span className="mt-1 leading-[12px] text-[11px]">{translate('img_use_as_ref_desc')}</span>
            </button>
            <button
              disabled={disableColorReference}
              onClick={handleSendToColorReference}
              className="flex flex-col w-full px-3 py-1.5 text-white hover:bg-primary hover:text-[#2c2c2c] disabled:pointer-events-none disabled:opacity-30"
            >
              <span className="text-xs leading-[16px] font-semibold">{translate('color_canvas_para1')}</span>
              <span className="mt-1 leading-[12px] text-[11px]">{translate('color_canvas_para2')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
